import type { PropInstance } from "../document/types.ts";
import type { FrameSize } from "./prop-hit.ts";

/**
 * Where a prop's art bounds above its base stand in the world (ADR-040): the box the occluder fade's cheap test and the
 * sight cone's test (ADR-061's amendment for P1.9-T75) both read, mirrored with the prop's flips; without art bounds
 * the whole frame width above the base stands in. Internal to the package; four numbers, so nothing is allocated.
 */

/**
 * Where a prop stands and how it is mirrored: all the geometry the art box reads from it.
 */
type Placement = Pick<PropInstance, "x" | "y" | "flipX" | "flipY">;

/**
 * The art box's width in pixels.
 *
 * @param size The frame.
 * @returns The width.
 */
export function artWidth(size: FrameSize): number {
  return size.art === undefined ? size.w : size.art.w;
}

/**
 * The art box's height in pixels: from the first opaque row to the base.
 *
 * @param size The frame.
 * @returns The height.
 */
export function artHeight(size: FrameSize): number {
  return size.base - (size.art === undefined ? 0 : size.art.y);
}

/**
 * The art box's left edge in world pixels, mirrored with the prop's `flipX`.
 *
 * @param prop The prop's anchor and flips.
 * @param size Its frame.
 * @param tileSize Tile size in pixels.
 * @returns The left edge.
 */
export function artLeft(prop: Placement, size: FrameSize, tileSize: number): number {
  const artX = size.art === undefined ? 0 : size.art.x;
  return prop.x * tileSize + (prop.flipX ? size.w - artX - artWidth(size) : artX);
}

/**
 * The art box's top edge in world pixels, mirrored with the prop's `flipY`.
 *
 * @param prop The prop's anchor and flips.
 * @param size Its frame.
 * @param tileSize Tile size in pixels.
 * @returns The top edge.
 */
export function artTop(prop: Placement, size: FrameSize, tileSize: number): number {
  return (prop.y + 1) * tileSize - size.h + (prop.flipY ? size.h - size.base : size.art === undefined ? 0 : size.art.y);
}
